import { useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import { isDialogOpen, shortcutRefusal } from '~/utils/textEntry';

export type UndoRedoShortcut = 'undo' | 'redo';

export interface ContourUndoRedoShortcutHandlers {
    /** The shortcuts do nothing at all while this is false. */
    enabled: boolean;
    canUndo: boolean;
    canRedo: boolean;
    undo: () => void;
    redo: () => void;
}

/**
 * Pure: which action, if any, a key event asks for. Undo is Ctrl+Z, redo is
 * Ctrl+Y, on every platform.
 *
 * Deliberately Ctrl-only, including on macOS. The Cmd pair cannot be completed
 * there — Cmd+Y is History in Safari — and a set where Cmd+Z undoes but its
 * counterpart does nothing is worse than no Cmd binding at all: the user finds
 * half a convention and has to discover that the other half is somewhere else.
 * Ctrl+Z and Ctrl+Y both work on macOS browsers, so one pair covers everyone
 * and one line of documentation describes it.
 *
 * Every other modifier is rejected. Alt in particular: Ctrl+Alt is AltGr on
 * several keyboard layouts, where Ctrl+Alt+Z is a character the user meant to
 * type, not an undo.
 */
export function shortcutFor(event: KeyboardEvent): UndoRedoShortcut | null {
    if (!event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
        return null;
    }
    const key = event.key.toLowerCase();
    if (key === 'z') {
        return 'undo';
    }
    if (key === 'y') {
        return 'redo';
    }
    return null;
}

/**
 * Binds Ctrl+Z and Ctrl+Y to the selected structure's undo and redo.
 *
 * Mounted by the toolbar's undo/redo control, so it is live exactly while
 * those buttons are on screen and shares their gating — there is no second
 * definition of when an undo is allowed, and no listener outstanding in the
 * registration or planning steps, where Ctrl+Z would otherwise revert a
 * contour the user is not even looking at.
 *
 * Inside a field Ctrl+Z is the browser's own undo, and behind a dialog or
 * under a half-drawn stroke a revert would change a contour the user cannot
 * see or is still drawing onto. The event is claimed (preventDefault) only
 * when an action actually runs, so every refusal {@link shortcutRefusal}
 * names leaves the key to whatever else would handle it.
 */
export function useContourUndoRedoShortcuts(
    handlers: ContourUndoRedoShortcutHandlers
): void {
    // The handlers are rebuilt every render; a ref keeps the listener stable
    // instead of resubscribing the window on each one.
    const latest = useRef(handlers);
    useEffect(() => {
        latest.current = handlers;
    });
    const toolManager =
        useRecoilValue(viewManagerState)?.interactionContext.toolManager;

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent): void => {
            const { enabled, canUndo, canRedo, undo, redo } = latest.current;
            if (!enabled) {
                return;
            }
            const action = shortcutFor(event);
            if (!action) {
                return;
            }
            const refusal = shortcutRefusal(
                event,
                document.activeElement,
                isDialogOpen(),
                toolManager?.isActiveToolActionInProgress() ?? false
            );
            if (refusal) {
                return;
            }
            if (action === 'undo' && canUndo) {
                event.preventDefault();
                undo();
            } else if (action === 'redo' && canRedo) {
                event.preventDefault();
                redo();
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [toolManager]);
}
